import { ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import LangSwitcher from './LangSwitcher';
import { Device } from '@/lib/device';
import { toggleTheme } from '@/lib/ui';

function DesktopTopbar({ title, breadcrumb, actions }: { title: string; breadcrumb?: string; actions?: ReactNode }) {
  return (
    <div className="border-b border-border px-6 py-3 flex items-center justify-between sticky top-0 bg-background/95 backdrop-blur z-10">
      <div>
        {breadcrumb && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-0.5">
            <span>{breadcrumb}</span>
          </div>
        )}
        <h1 className="text-xl font-bold flex items-center gap-2">{title}</h1>
      </div>
      <div className="flex items-center gap-3">
        <LangSwitcher />
        <button
          onClick={toggleTheme}
          className="h-9 w-9 rounded-md border border-border flex items-center justify-center hover:bg-accent"
          aria-label="Toggle theme"
          title="Theme"
        >
          🌓
        </button>
        {actions}
      </div>
    </div>
  );
}

export function PageShell({
  device,
  title,
  breadcrumb,
  actions,
  children,
  mobileHeaderRight,
}: {
  device: Device;
  title: string;
  breadcrumb?: string;
  actions?: ReactNode;
  children: ReactNode;
  mobileHeaderRight?: ReactNode;
}) {
  if (device === 'mobile') {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <header className="flex items-center justify-between px-4 py-3 border-b border-border gap-2 sticky top-0 bg-background/95 backdrop-blur z-30">
          <div className="text-center flex-1 min-w-0">
            <div className="font-bold text-sm truncate">{title}</div>
            {breadcrumb && <div className="text-[10px] text-muted-foreground">{breadcrumb}</div>}
          </div>
          {mobileHeaderRight}
        </header>
        <main className="flex-1 pb-24">{children}</main>
        <MobileNav />
      </div>
    );
  }
  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0">
        <DesktopTopbar title={title} breadcrumb={breadcrumb} actions={actions} />
        <div className="flex-1 overflow-auto">{children}</div>
      </main>
    </div>
  );
}

export default function Layout() {
  const loc = useLocation();
  // Hide shell on login
  if (loc.pathname === '/login') return <Outlet />;
  return <Outlet />;
}
